import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Shield } from "lucide-react";

const Navbar = ({ transparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/storia", label: "Storia" },
    { path: "/organigramma", label: "Organigramma" },
    { path: "/prima-squadra", label: "Prima Squadra" },
    { path: "/giovanili", label: "Settore Giovanile" },
    { path: "/news", label: "News" },
    { path: "/sponsor", label: "Partner Ufficiali" },
    { path: "/contatti", label: "Contatti" },
  ];

  const isActive = (path) => location.pathname === path;

  const bgClass = transparent && !isScrolled
    ? "bg-transparent"
    : "bg-white/95 backdrop-blur-md shadow-lg";

  const textClass = transparent && !isScrolled
    ? "text-white"
    : "text-slate-800";

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_volpiano-calcio/artifacts/8nz2bql1_IMG_8381.webp" 
              alt="G.S.D. Volpiano Pianese"
              className="h-12 lg:h-14 w-auto"
            />
            <div className={`hidden sm:block ${textClass}`}>
              <span className="font-bold text-lg lg:text-xl tracking-tight">VOLPIANOPIANESE</span>
              <span className="block text-xs lg:text-sm font-medium opacity-80">G.S.D.</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`nav-link ${textClass} ${
                  isActive(link.path)
                    ? "text-[#0047AB] font-semibold"
                    : "hover:text-[#0047AB]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${textClass}`}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white shadow-xl animate-fade-in"
        >
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                  isActive(link.path)
                    ? "bg-[#0047AB] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
